/**
 * Edit Layout Dialog - New Implementation
 *
 * Edit layout properties: name, asset types, and image.
 * Simplified without language dictionary - uses direct field values.
 */
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Input, Select } from "@gpstrackit/gtc-react-controls";
import {
	Box,
	Typography,
	Menu,
	MenuItem,
	ListItemText,
	Divider,
	Button as MuiButton,
	CircularProgress,
	Paper,
} from "@mui/material";
import {
	ArrowDropDown as ArrowDropDownIcon,
	CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { searchAssetSubtypes, getAssetViewImage, getAssetViews } from "../../../../../../services-px/configApi";

/**
 * Strip brackets from asset type strings
 * Fixes bug where asset types are stored with nested brackets like [[[Standard]]]
 */
const stripBrackets = (value) => {
	if (typeof value !== "string") return value;
	return value.replace(/^\[+|\]+$/g, "");
};

/**
 * Dialog for editing asset layout properties
 */
const EditLayoutDialog = ({ open, onClose, onSave, layout, allLayouts }) => {
	const [formData, setFormData] = useState({
		name: "",
		assetType: [],
		assetViewId: "",
	});
	const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
	const [availableImages, setAvailableImages] = useState([]);
	const [uploadError, setUploadError] = useState("");
	const [fileUploaded, setFileUploaded] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [imagePreviewLoading, setImagePreviewLoading] = useState(false);
	const fileInputRef = useRef(null);

	// Track the original assetViewId when dialog opens (so it's always an option)
	const [originalAssetViewId, setOriginalAssetViewId] = useState(null);

	// Asset subtypes dropdown state
	const [assetSubtypes, setAssetSubtypes] = useState([]);
	const [assetSubtypesLoading, setAssetSubtypesLoading] = useState(false);

	// Asset views (images) loading state
	const [assetViewsLoading, setAssetViewsLoading] = useState(false);

	// Fetch asset subtypes and asset views when dialog opens
	useEffect(() => {
		if (open) {
			// Fetch asset subtypes
			setAssetSubtypesLoading(true);
			searchAssetSubtypes()
				.then((subtypes) => {
					// Filter to ensure unique, valid IDs for the Select component
					const seenIds = new Set();
					const validSubtypes = (subtypes || []).filter((subtype) => {
						if (!subtype?.id || !subtype?.name || seenIds.has(subtype.id)) {
							return false;
						}
						seenIds.add(subtype.id);
						return true;
					});
					setAssetSubtypes(validSubtypes);
				})
				.catch((error) => {
					console.error("Failed to fetch asset subtypes:", error);
					setAssetSubtypes([]);
				})
				.finally(() => {
					setAssetSubtypesLoading(false);
				});

			// Fetch asset views (images) from API
			setAssetViewsLoading(true);
			getAssetViews()
				.then((views) => {
					const images = (views || [])
						.filter((view) => view?.id)
						.map((view) => ({
							id: view.id,
							name: view.name || view.id,
						}));
					setAvailableImages(images);
				})
				.catch((error) => {
					console.error("Failed to fetch asset views:", error);
					setAvailableImages([]);
				})
				.finally(() => {
					setAssetViewsLoading(false);
				});
		}
	}, [open]);

	useEffect(() => {
		if (open && layout) {
			// Use direct field values - no language dictionary
			// Strip brackets from asset types to fix display bug
			const rawAssetTypes = layout.assetType
				? [...(Array.isArray(layout.assetType) ? layout.assetType : [layout.assetType])]
				: [];
			const cleanedAssetTypes = rawAssetTypes.map(stripBrackets);

			setFormData({
				name: layout.layoutName || "",
				assetType: cleanedAssetTypes,
				assetViewId: layout.assetViewId || "",
			});
			setFileUploaded(null);
			setUploadError("");

			// Track the original assetViewId so it's always available as an option
			setOriginalAssetViewId(layout.assetViewId || null);
		}
	}, [open, layout]);

	// Ensure current layout's assetViewId is included in available images
	useEffect(() => {
		if (open && layout?.assetViewId && !assetViewsLoading) {
			setAvailableImages((prev) => {
				const exists = prev.some((img) => img.id === layout.assetViewId);
				if (!exists) {
					return [...prev, { id: layout.assetViewId, name: layout.layoutName || layout.assetViewId }];
				}
				return prev;
			});
		}
	}, [open, layout, assetViewsLoading]);

	// Load image preview when assetViewId changes
	useEffect(() => {
		const loadImagePreview = async () => {
			// If we have an uploaded file, use its data URL
			if (fileUploaded?.assetViewData) {
				setImagePreview(fileUploaded.assetViewData);
				return;
			}

			// If no assetViewId or it's the pending upload marker, clear preview
			if (!formData.assetViewId || formData.assetViewId === "default-selected-id") {
				setImagePreview(null);
				return;
			}

			// Fetch the image from the API
			setImagePreviewLoading(true);
			// Clear previous preview before loading new one
			setImagePreview(null);
			try {
				const svgContent = await getAssetViewImage(formData.assetViewId);
				setImagePreview(svgContent);
			} catch (error) {
				console.error("Failed to load image preview:", error);
				setImagePreview(null);
			} finally {
				setImagePreviewLoading(false);
			}
		};

		if (open) {
			loadImagePreview();
		}
	}, [formData.assetViewId, fileUploaded, open]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleAssetTypeSelect = (selected) => {
		if (!selected) return;

		const selectedName = selected.name || selected.label;
		if (selectedName) {
			// Single selection - replace current value
			setFormData((prev) => ({
				...prev,
				assetType: [selectedName],
			}));
		}
	};

	const handleImageMenuOpen = (event) => {
		setImageMenuAnchor(event.currentTarget);
	};

	const handleImageMenuClose = () => {
		setImageMenuAnchor(null);
	};

	const handleUploadNewImage = () => {
		handleImageMenuClose();
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const handleFileSelect = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		setUploadError("");

		// Validate file type
		const validTypes = ["image/png", "image/jpg", "image/jpeg", "image/svg+xml"];
		if (!validTypes.includes(file.type)) {
			setUploadError("Invalid file type. Please upload PNG, JPG, JPEG, or SVG files.");
			return;
		}

		// Validate file size (max 5MB)
		const maxSize = 5 * 1024 * 1024;
		if (file.size > maxSize) {
			setUploadError("File is too large. Maximum size is 5MB.");
			return;
		}

		try {
			const fileReader = new FileReader();

			fileReader.onload = () => {
				try {
					const assetViewData = fileReader.result;
					const fileName = file.name.replace(/\.[^/.]+$/, "");

					const newFileUploaded = {
						assetViewName: fileName,
						proprietary: false,
						assetViewData: assetViewData,
						// companyId is inferred by the backend from the auth token
						companyId: localStorage.getItem('companyId'),
					};

					setFileUploaded(newFileUploaded);

					// Use marker ID for pending upload
					setFormData((prev) => ({
						...prev,
						assetViewId: "default-selected-id",
					}));

					// Add to available images for display
					setAvailableImages((prev) => [
						...prev.filter((img) => img.id !== "default-selected-id"),
						{ id: "default-selected-id", name: fileName },
					]);
				} catch (error) {
					console.error("Image read error:", error);
					setUploadError(error.message || "Failed to read file. Please try again.");
				}
			};

			fileReader.onerror = () => {
				setUploadError("Failed to read file. Please try again.");
			};

			fileReader.readAsDataURL(file);
		} catch (error) {
			console.error("Image read error:", error);
			setUploadError(error.message || "Failed to read file. Please try again.");
		} finally {
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleSelectImage = (imageId) => {
		// Clear uploaded file if selecting a different existing image
		if (imageId !== "default-selected-id") {
			setFileUploaded(null);
		}
		setFormData((prev) => ({
			...prev,
			assetViewId: imageId,
		}));
		handleImageMenuClose();
	};

	const getSelectedImageName = () => {
		if (!formData.assetViewId) return "None";
		const foundImage = availableImages.find((img) => img.id === formData.assetViewId);
		return foundImage ? foundImage.name : formData.assetViewId;
	};

	const handleSave = () => {
		if (!formData.name.trim()) {
			return;
		}

		// Normalize assetType by stripping any nested brackets before saving
		const normalizedAssetType = formData.assetType.map(stripBrackets);

		onSave(
			{
				name: formData.name.trim(),
				assetType: normalizedAssetType,
				assetViewId: formData.assetViewId,
			},
			fileUploaded
		);

		handleClose();
	};

	const handleClose = () => {
		setFormData({ name: "", assetType: [], assetViewId: "" });
		setImageMenuAnchor(null);
		setUploadError("");
		setFileUploaded(null);
		setImagePreview(null);
		setImagePreviewLoading(false);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		onClose();
	};

	const isFormValid = () => {
		return formData.name.trim().length > 0 && formData.assetType.length > 0;
	};

	return (
		<Modal
			title="Edit Asset Layout"
			open={open}
			handleClose={handleClose}
			size="small"
			footer={
				<div>
					<Button
						variant="secondary"
						onClick={handleClose}
						style={{ marginRight: "5px" }}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						isDisabled={!isFormValid()}
					>
						Save Changes
					</Button>
				</div>
			}
		>
			<div style={{ padding: "15px" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
					<Input
						label="Name"
						value={formData.name}
						onChange={(value) => handleChange("name", value)}
						required
						autoFocus
					/>

					{/* Hidden file input */}
					<input
						ref={fileInputRef}
						type="file"
						accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/jpg,image/svg+xml"
						onChange={handleFileSelect}
						style={{ display: "none" }}
					/>

					{/* Image Selection */}
					<Box>
						<Typography
							variant="body2"
							sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}
						>
							Image
						</Typography>
						<MuiButton
							variant="outlined"
							onClick={handleImageMenuOpen}
							endIcon={<ArrowDropDownIcon />}
							fullWidth
							sx={{
								justifyContent: "space-between",
								textTransform: "none",
								color: "text.primary",
								borderColor: "rgba(0, 0, 0, 0.23)",
								backgroundColor: "transparent",
								py: 1,
								"&:hover": {
									borderColor: "rgba(0, 0, 0, 0.87)",
									backgroundColor: "rgba(0, 0, 0, 0.04)",
								},
							}}
						>
							{getSelectedImageName()}
						</MuiButton>
						{uploadError && (
							<Typography variant="caption" color="error" sx={{ mt: 1 }}>
								{uploadError}
							</Typography>
						)}
						<Menu
							anchorEl={imageMenuAnchor}
							open={Boolean(imageMenuAnchor)}
							onClose={handleImageMenuClose}
							PaperProps={{
								style: {
									maxHeight: 400,
									width: "300px",
								},
							}}
						>
							<MenuItem key="upload-new" onClick={handleUploadNewImage}>
								<CloudUploadIcon sx={{ mr: 1 }} />
								<ListItemText
									primary="Upload new image"
									secondary="PNG, JPG, JPEG, SVG (max 5MB)"
								/>
							</MenuItem>
							{availableImages.length > 0 && (
								<>
									<Divider />
									<MenuItem onClick={() => handleSelectImage("")}>
										<ListItemText primary="None" />
									</MenuItem>
								</>
							)}
							{availableImages.map((image) => (
								<MenuItem
									key={image.id}
									onClick={() => handleSelectImage(image.id)}
									selected={formData.assetViewId === image.id}
								>
									<ListItemText primary={image.name} />
								</MenuItem>
							))}
						</Menu>
					</Box>

					<Select
						id="asset-type-select"
						label="Asset type"
						placeholder="Select asset type"
						value={
							assetSubtypes.find((s) => s.name === formData.assetType[0]) ||
							(formData.assetType[0] ? { name: formData.assetType[0], id: formData.assetType[0] } : null)
						}
						onChange={handleAssetTypeSelect}
						options={assetSubtypes}
						textField="name"
						valueField="id"
						isLoading={assetSubtypesLoading}
						isClearable={false}
						required
					/>

					{/* Image Preview */}
					{imagePreviewLoading && (
						<Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
							<CircularProgress size={24} />
						</Box>
					)}
					{!imagePreviewLoading && imagePreview && (
						<Paper
							variant="outlined"
							sx={{
								p: 1,
								backgroundColor: "#f5f5f5",
								overflow: "hidden",
								display: "flex",
								justifyContent: "center",
							}}
						>
							{fileUploaded?.assetViewData ? (
								<img
									src={fileUploaded.assetViewData}
									alt="Preview"
									style={{
										maxWidth: "100%",
										height: "auto",
										display: "block",
									}}
								/>
							) : (
								<Box
									dangerouslySetInnerHTML={{ __html: imagePreview }}
									sx={{
										"& svg": {
											maxWidth: "100%",
											height: "auto",
											display: "block",
										},
									}}
								/>
							)}
						</Paper>
					)}
				</Box>
			</div>
		</Modal>
	);
};

export default EditLayoutDialog;
